import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { getToken, setToken, clearToken } from '../lib/authToken';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Send the session token as a Bearer header when we have one (cross-subdomain safe).
const authHeaders = () => {
  const t = getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    // No token → not logged in; skip the round-trip.
    if (!getToken()) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
        headers: authHeaders(),
      });
      setUser(response.data);
    } catch (error) {
      // Token invalid/expired — drop it so we don't keep retrying a dead session.
      clearToken();
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  // Real Google Sign-In: exchange the Google id_token for our session token.
  // Returns the user on success; throws on failure so the caller can show an error.
  const loginWithGoogle = async (idToken) => {
    const response = await axios.post(
      `${API}/auth/google`,
      { id_token: idToken },
      { withCredentials: true }
    );
    const { session_token, user: u } = response.data || {};
    if (session_token) setToken(session_token);
    setUser(u || null);
    return u;
  };

  const login = () => {
    // Send users to OUR login page (Google Sign-In), NOT the Emergent template's mock auth.
    // Every "Login"/"Sign in" button across the app funnels here.
    window.location.href = '/login';
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, {
        withCredentials: true,
        headers: authHeaders(),
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearToken();
      setUser(null);
      window.location.href = '/';
    }
  };

  const updateUser = (updates) => {
    setUser(prev => ({ ...prev, ...updates }));
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, loginWithGoogle, logout, updateUser, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};
